import InvalidDateException from '~/errors/InvalidDateException';

import LocalStorageService from '~/services/localStorage.service';

import {
  type DateRangeOption,
  isPredefinedDateRange,
} from '~/ui/molecules/DateInput';

import { type DateLike, type DateRange, dayjs } from './datetime';
import { getDatesForPredefinedDateRange } from './filters';
import { date4server } from './l10n';
import { log } from './logging';

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD

/**
 * Formats an arbitrary stored value for a native `<input type="date">`, which
 * only renders strings in `YYYY-MM-DD`.
 *
 * Already-valid `YYYY-MM-DD` strings are passed through untouched. This is
 * critical: a native date input emits a complete value on every keystroke while
 * the year is being typed (e.g. `0002-06-08`, `0020-06-08`), and running those
 * through dayjs would apply the legacy 2-digit-year → 1900s offset
 * (`0002-06-08` → `1902-06-08`), making it impossible to type the year.
 * Only non-conforming values (e.g. the backend's ISO timestamps) are converted.
 * Non-string inputs (`Date`, numeric timestamps) are parsed via dayjs and
 * formatted, falling back to `''` when they are not valid dates.
 */
export const formatForDateInput = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'string' && ISO_DATE_REGEX.test(value)) {
    return value;
  }

  const parsedDate = dayjs(value as DateLike);

  return parsedDate.isValid() ? parsedDate.format(date4server) : '';
};

export const startOfDay = (date: DateLike) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);

  return newDate?.getTime();
};

export const endOfDay = (date: DateLike) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);

  return newDate?.getTime();
};

export const parseDate = (date: DateLike | undefined) => {
  if (!date) {
    return null;
  }

  // Handle dates as timestamp stored in a string.
  const number_ = Number(date);
  if (Number.isFinite(number_)) {
    return new Date(number_);
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

/**
 * Parses a date input and returns it as a JavaScript Date object in local time.
 *
 * Note: JavaScript's Date constructor automatically handles timezone conversion,
 * so UTC dates from the server will be automatically converted to local time.
 *
 * @param date - The date to parse. Can be:
 *   - ISO string (e.g., "2024-03-20" or "2024-03-20T15:00:00Z")
 *   - Timestamp number
 *   - Date object
 * @returns - A Date object in local time, or null if the input is undefined, null, or invalid.
 */
export const fromUTC = (date: DateLike | undefined) => {
  if (!date) {
    return null;
  }

  let parsedDate;

  // Parse the input date
  if (typeof date === 'string' || typeof date === 'number') {
    parsedDate = new Date(date);
  } else if (date instanceof Date) {
    parsedDate = new Date(date.getTime());
  } else {
    return null;
  }

  // Check if the parsed date is valid
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  /**
   * If date is a string without time information, set the time to midnight.
   *
   * This handles the case that a simple ISO date is sent by the server,
   * which is meant to represent start of day of that date,
   * but our UTC-to-local-time conversion would interpret the input as start of day
   * and add the timezone offset.
   */
  if (typeof date === 'string' && ISO_DATE_REGEX.test(date)) {
    parsedDate.setHours(0, 0, 0, 0);
  }

  return parsedDate;
};

/**
 * Converts a given date string into UTC format.
 *
 * Offsets timezone from user's local time into UTC time
 *
 * @param date - The date to convert to UTC.
 * @returns - The equivalent date in UTC time, or null if the input date is undefined or null.
 */
export const toUTC = (date: DateLike | undefined) => {
  if (!date) {
    return null;
  }

  let parsedDate;

  // Parse the input date
  if (typeof date === 'string' || typeof date === 'number') {
    parsedDate = new Date(date);
  } else if (date instanceof Date) {
    parsedDate = new Date(date.getTime());
  } else {
    return null;
  }

  // Check if the parsed date is valid
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  // If the date is a string without time information, set the time to midnight UTC
  if (typeof date === 'string' && ISO_DATE_REGEX.test(date)) {
    parsedDate.setUTCHours(0, 0, 0, 0);
  }

  return parsedDate;
};

class DateUtils {
  readonly DATE_FORMAT = {
    DATE_TIME_INPUT_FORMAT: 'YYYY-MM-DD[T]HH:mm',
    DD_MM: 'DD.MM',
    DD_MM_YY: 'DD.MM.YY',
    DD_MM_YYYY: 'DD.MM.YYYY',
    DD_MM_YYYY__HH_mm: 'DD.MM.YYYY HH:mm',
    DD_MM_YYYY__HH_mm_ss: 'DD.MM.YYYY HH:mm:ss',
    HH: 'HH',
    HH_mm_ss: 'HH:mm:ss',
    mm: 'mm',
    MMM_YYYY: 'MMM YYYY',
    MMMM_YYYY: 'MMMM YYYY',
    YYYY_MM_DD: 'YYYY.MM.DD',
    YYYY_MM_DD__HH_mm_ss_SSSSSS: 'YYYY-MM-DD HH:mm:ss.SSSSSS',
    YYYY_MM_DD_ISO: 'YYYY-MM-DD',
    YYYYMMDD: 'YYYYMMDD',
    YYYYMMDD_HHMMSS: 'YYYYMMDD_HHmmss',
    YYYYMMDDHHMMSS: 'YYYYMMDDHHmmss',
  };

  constructor() {}

  getFormattedDate = (date: DateLike, format: string) => {
    const value = dayjs(date);

    if (!value.isValid()) {
      throw new InvalidDateException();
    }

    return value.format(format);
  };

  getFormattedDate_safe = (
    date: DateLike,
    outputFormat: string,
    inputFormat?: string,
  ) => {
    if (date == null) {
      return null;
    }

    const value = dayjs(date, inputFormat);

    if (!value.isValid()) {
      return null;
    }

    return value.format(outputFormat);
  };

  getFormattedDateWithoutMidnight_safe = (
    date: DateLike,
    outputFormat: string,
    inputFormat: string,
  ) => {
    if (date == null) {
      return null;
    }

    const value = dayjs(date, inputFormat);

    if (!value.isValid()) {
      return null;
    }

    const formattedValue = value.format(outputFormat);

    if (
      formattedValue.split(' ')[1] === '00:00' ||
      formattedValue.split(' ')[1] === '00:00:00'
    ) {
      return formattedValue.split(' ')[0];
    }

    return formattedValue;
  };

  getNumberOfDays(from: number, to: number) {
    return Math.round((to - from) / (1000 * 60 * 60 * 24)) + 1;
  }

  /**
   * Converts the selected date range into a timeframe object for data filtering.
   *
   * @param selectedDateRange - An array representing the selected date range.
   * @return The timeframe object with 'from' and 'to' properties.
   */
  extractTimeframe(selectedDateRange: DateRange) {
    if (!selectedDateRange?.[0] || !selectedDateRange[1]) {
      log.error({
        message: `Failed to extract timeframe from date range. date range: ${JSON.stringify(
          selectedDateRange,
        )}`,
        error: new Error(
          `Failed to extract timeframe from date range. date range: ${JSON.stringify(
            selectedDateRange,
          )}`,
        ),
      });

      const from = new Date();
      from.setFullYear(from.getFullYear() - 100); // subtract 100 years per default
      const to = new Date();

      return {
        from: from.getTime(),
        to: to.getTime(),
      };
    }

    const fromDate = parseDate(selectedDateRange[0]);
    const toDate = parseDate(selectedDateRange[1]);

    if (!fromDate || !toDate) {
      throw new InvalidDateException();
    }

    const timeframe = {
      from: startOfDay(fromDate),
      to: endOfDay(toDate),
    };

    return timeframe;
  }

  onTheSameDay(day1: DateLike, day2: DateLike) {
    return (
      new Date(day1).setHours(0, 0, 0, 0) ===
      new Date(day2).setHours(0, 0, 0, 0)
    );
  }

  /**
   * For a predefined range the stored label ('yesterday', 'week', ...) is the source of
   * truth and the stored absolute dates are only a cache of it, so the dates are derived
   * from the label on every load. The cache used to be trusted whenever it had been written
   * on the same calendar day, which let the two drift: the picker read 'Gestern' while the
   * request asked for a different day, and it stayed that way until midnight (COR-367).
   */
  getDateRangeFromLocalStorage(
    name: string,
    individualDateRange: boolean,
    predefinedDateRange: DateRangeOption,
  ) {
    if (!individualDateRange && isPredefinedDateRange(predefinedDateRange)) {
      const [startDate, endDate] =
        getDatesForPredefinedDateRange(predefinedDateRange);

      this.setDateRangeInLocalStorage(name, [startDate!, endDate!]);

      return [startDate, endDate];
    }

    const dateRangeObject = LocalStorageService.getObjectFromLocalStorage(name);

    if (!dateRangeObject) {
      return getDatesForPredefinedDateRange(predefinedDateRange);
    }

    const dateRange = dateRangeObject.data.split(
      LocalStorageService.SEPARATOR,
    ) as DateRange;

    return dateRange.map((date: string) => parseDate(date));
  }

  setDateRangeInLocalStorage(cookieName: string, dateRange: DateRange) {
    LocalStorageService.setMetaDataLocalStorage(
      cookieName,
      dateRange.join(LocalStorageService.SEPARATOR),
    );
  }

  getDifferenceInHours(date1: DateLike, date2: DateLike) {
    const diffInMilliseconds =
      new Date(date2).getTime() - new Date(date1).getTime();
    // Convert the time difference from milliseconds to hours
    const diffInHours = diffInMilliseconds / (1000 * 60 * 60);

    return diffInHours;
  }
}

export const dateUtils = new DateUtils();
